import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useEffect } from 'react';

import { BesluitSchema } from '@/api';
import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluitId } from '@/api/queries/besluit';
import { useHoofdstukHB } from '@/api/queries/objects';
import { GRONDSLAGEN_MAP } from '@/constants/grondslagen';
import { useOffsetTop } from '@/utils/hooks/useOffsetTop';

import { Body } from '../-components/Body';
import { ChapterNavigation } from '../-components/ChapterNavigation';
import { NoFilterResultWarning } from '../-components/NoFilterResultWarning';
import { NoSelectedLabelWarning } from '../-components/NoSelectedLabelWarning';
import { ViewerHoofdstuk } from '../-components/ViewerHoofdstuk';
import { ViewerParagraaf } from '../-components/ViewerParagraaf';
import { ViewerSubparagraaf } from '../-components/ViewerSubparagraaf';
import { ViewerVoorschrift } from '../-components/ViewerVoorschrift';
import { getObjectStatus } from '../-components/utils/getObjectState';
import useViewerStore, { SidebarLeftTab } from '../-utils/useViewerStore';
import { viewerRouteApi } from '../-utils/viewerRouteApi';
import { useBesluitHoofdstukData } from './-utils/useBesluitHoofdstukData';
import { useFindHoofdstukInGrondslagen } from './-utils/useFindHoofdstukInGrondslagen';
import { useViewerBesluitFilterGrondslagen } from './-utils/useViewerBesluitFilterGrondslagen';

/**
 * Test ID utils
 */
const paragraafTestId = (paragraafId: string) => `paragraaf-${paragraafId}`;
const subparagraafTestId = (subparagraafId: string) =>
  `subparagraaf-${subparagraafId}`;
const voorschriftTestId = (voorschriftId: string) =>
  `voorschrift-${voorschriftId}`;

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/besluit/$besluitId/_layout/labels/$hoofdstukId'
)({
  loader: async ({
    params: { besluitId, bedrijfLineageId, hoofdstukId },
    context: { queryClient, labelIds },
  }) => {
    const [latestBedrijf, besluit] = await Promise.all([
      queryClient.ensureQueryData(
        useLatestBedrijf.getOptions(bedrijfLineageId)
      ),
      queryClient.ensureQueryData(useBesluitId.getOptions(besluitId)),
      queryClient.ensureQueryData(
        useHoofdstukHB.getOptions({ besluitId, hoofdstukId })
      ),
      labelIds.length > 0
        ? Promise.all([
            queryClient.ensureQueryData(
              useViewerBesluitFilterGrondslagen.getOptions({
                besluitId,
                labelIds,
              })
            ),
            queryClient.ensureQueryData(
              useBesluitHoofdstukData.getOptions({
                hoofdstukId,
                besluitId,
                labelIds,
              })
            ),
          ])
        : [],
    ]);

    if (!latestBedrijf || !besluit) throw notFound();
  },
  onEnter: () => {
    useViewerStore.getState().setSidebarLeftTabId(SidebarLeftTab.FILTER);
  },
  component: LabelsComponent,
});

/**
 * Route component
 */
function LabelsComponent() {
  const { besluitId, hoofdstukId, bedrijfLineageId } = Route.useParams();
  const labelIds = viewerRouteApi.root.useSearch({
    select: (search) => search.labelIds ?? [],
  });

  /**
   * Fetch data
   */
  const bedrijf = useLatestBedrijf(bedrijfLineageId);
  const besluit = useBesluitId(besluitId, { staleTime: Infinity });

  /**
   * Catch loading states
   */
  if (!besluit.data || !bedrijf.data) return null;

  return (
    <>
      <Body.ContentHeader sticky metadataSwitch>
        <Body.BesluitTitle besluit={besluit.data} />
      </Body.ContentHeader>

      <Body.BesluitDescriptionList besluit={besluit.data} />

      {labelIds.length === 0 ? (
        <div className="mt-8 flex justify-center">
          <NoSelectedLabelWarning />
        </div>
      ) : (
        <ParagraafList
          labelIds={labelIds}
          hoofdstukId={hoofdstukId}
          besluit={besluit.data}
        />
      )}
    </>
  );
}

const ParagraafList = ({
  labelIds,
  hoofdstukId,
  besluit,
}: {
  labelIds: string[];
  hoofdstukId: string;
  besluit: BesluitSchema;
}) => {
  const besluitId = besluit.ID;

  const links = useQuery({
    ...useBesluitHoofdstukData.getOptions({
      hoofdstukId,
      besluitId,
      labelIds,
    }),
    // Keep previous data to prevent flickering
    placeholderData: keepPreviousData,
  });

  const grondslagen = useQuery({
    ...useViewerBesluitFilterGrondslagen.getOptions({
      besluitId,
      labelIds,
    }),
    // Keep previous data to prevent flickering
    placeholderData: keepPreviousData,
  });

  const HB = useHoofdstukHB(
    { besluitId, hoofdstukId },
    { staleTime: Infinity }
  );

  const { hoofdstuk, nextHoofdstuk, previousHoofdstuk } =
    useFindHoofdstukInGrondslagen({
      grondslagen: grondslagen.data ?? [],
      hoofdstukId,
    });

  const { ref: listRef, offsetTop } = useOffsetTop();
  const setScrollToParagraaf = useViewerStore((s) => s.setScrollToParagraaf);

  const virtualizer = useWindowVirtualizer({
    count: links.data?.length ?? 0,
    estimateSize: () => 1000,
    overscan: 2,
    enabled: !!links.data?.length,
    paddingEnd: 200,
    initialOffset: 0,
    scrollMargin: offsetTop,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  useEffect(() => {
    setScrollToParagraaf((paragraafId: string) => {
      const index = links.data?.findIndex(
        (link) => link.data.ID === paragraafId
      );
      if (index !== -1 && index !== undefined) {
        virtualizer.scrollToIndex(index, { align: 'start' });
      }
    });

    return () => setScrollToParagraaf(null);
  }, [links, virtualizer, setScrollToParagraaf]);

  if (links.data?.length === 0) {
    return (
      <div className="mt-8 flex justify-center">
        <NoFilterResultWarning />
      </div>
    );
  }

  if (!hoofdstuk || !HB.data) {
    return null;
  }

  return (
    <>
      {totalSize === 0 ? (
        <ViewerHoofdstuk
          className="mt-2"
          hoofdstuk={hoofdstuk}
          besluitTypeId={besluit.Type}
          HB={HB.data}
          mode="besluit"
        />
      ) : (
        <div
          className="relative w-full"
          style={{
            height: totalSize,
          }}
          ref={listRef}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${(virtualItems[0]?.start ?? 0) - offsetTop}px)`,
            }}
            className="space-y-2 pt-2"
          >
            <ViewerHoofdstuk
              hoofdstuk={hoofdstuk}
              besluitTypeId={besluit.Type}
              HB={HB.data}
              mode="besluit"
            />

            {virtualItems.map((item) => {
              const paragraaf = links.data?.[item.index];

              if (!paragraaf) return null;

              return (
                <ViewerParagraaf
                  key={item.key}
                  ref={virtualizer.measureElement}
                  data-index={item.index}
                  besluitTypeId={besluit.Type}
                  hoofdstukNummer={hoofdstuk.Nummer}
                  paragraaf={paragraaf.data}
                  PHB={paragraaf.PHB}
                  mode="besluit"
                  testId={paragraafTestId(paragraaf.data.ID)}
                  linkSlot={
                    <Link
                      to="/$bedrijfLineageId/viewer/besluit/$besluitId/voorschriften/$hoofdstukId"
                      from={Route.fullPath}
                      search={{
                        focusParagraafId: paragraaf.data.ID,
                      }}
                    >
                      Bekijk ongefilterde paragraaf
                    </Link>
                  }
                >
                  <div className="space-y-2 py-2">
                    {paragraaf.subparagrafen.map((subparagraaf) => {
                      return (
                        <ViewerSubparagraaf
                          besluitTypeId={besluit.Type}
                          subparagraaf={subparagraaf.data}
                          SPPB={subparagraaf.SPPB}
                          hasVoorschriften={
                            subparagraaf.voorschriften.length > 0
                          }
                          mode="besluit"
                          testId={subparagraafTestId(subparagraaf.data.ID)}
                          key={`viewer-labels-subparagraaf-${subparagraaf.data.ID}`}
                        >
                          <div className="space-y-2 py-2">
                            {subparagraaf.voorschriften.map((voorschrift) => {
                              const state = getObjectStatus(
                                voorschrift.data,
                                true
                              );

                              return (
                                <ViewerVoorschrift
                                  key={`viewer-labels-voorschrift-${voorschrift.data.ID}`}
                                  hoofdstuk={hoofdstuk}
                                  testId={voorschriftTestId(
                                    voorschrift.VSPB.Voorschrift
                                  )}
                                  state={
                                    state
                                      ? {
                                          besluitTypeId: besluit.Type,
                                          state,
                                        }
                                      : undefined
                                  }
                                  VSPB={voorschrift.VSPB}
                                  paragraafId={paragraaf.data.ID}
                                  paragraafNummer={paragraaf.data.Nummer}
                                  voorschrift={voorschrift.data}
                                  mode="besluit"
                                  highlightedLabelIds={labelIds}
                                />
                              );
                            })}
                          </div>
                        </ViewerSubparagraaf>
                      );
                    })}
                  </div>
                </ViewerParagraaf>
              );
            })}
          </div>
        </div>
      )}

      <ChapterNavigation
        previous={
          previousHoofdstuk ? (
            <ChapterNavigation.Previous
              nummer={previousHoofdstuk.Nummer}
              htmlTitle={previousHoofdstuk.Titel}
              grondslag={GRONDSLAGEN_MAP.get(previousHoofdstuk.Grondslag)}
              subtitle="Vorig hoofdstuk"
              to="/$bedrijfLineageId/viewer/besluit/$besluitId/labels/$hoofdstukId"
              from="/$bedrijfLineageId/viewer/besluit/$besluitId"
              params={{
                hoofdstukId: previousHoofdstuk.ID,
              }}
            />
          ) : undefined
        }
        next={
          nextHoofdstuk ? (
            <ChapterNavigation.Next
              nummer={nextHoofdstuk.Nummer}
              htmlTitle={nextHoofdstuk.Titel}
              grondslag={GRONDSLAGEN_MAP.get(nextHoofdstuk.Grondslag)}
              subtitle="Volgend hoofdstuk"
              to="/$bedrijfLineageId/viewer/besluit/$besluitId/labels/$hoofdstukId"
              from="/$bedrijfLineageId/viewer/besluit/$besluitId"
              params={{
                hoofdstukId: nextHoofdstuk.ID,
              }}
            />
          ) : undefined
        }
      />
    </>
  );
};
